import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type TrackerSettingsView,
  type UpdateTrackerSettingsInput,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { FormField } from '../components/FormField';
import { TextInput } from '../components/TextInput';
import { SelectField } from '../components/SelectField';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { isApiError } from '../api/errors';
import { toDisplayError, toFieldErrors } from '../api/errorMessages';
import { INTEGRATION_ASSURANCE } from '../constants/integrationCopy';
import { AuthedBrand } from './AuthedBrand';
import styles from './ProjectScreen.module.css';

/** Providers exposed in the UI (the backend also supports GitLab, kept hidden). */
type UiProvider = 'jira' | 'github' | 'linear';

const PROVIDERS: ReadonlyArray<{ id: UiProvider; label: string; keyUrl: string; keyLabel: string }> = [
  {
    id: 'jira',
    label: 'Jira',
    keyUrl: 'https://id.atlassian.com/manage-profile/security/api-tokens',
    keyLabel: 'Create a Jira API token',
  },
  {
    id: 'github',
    label: 'GitHub Issues',
    keyUrl: 'https://github.com/settings/tokens',
    keyLabel: 'Create a GitHub access token',
  },
  {
    id: 'linear',
    label: 'Linear',
    keyUrl: 'https://linear.app/settings/api',
    keyLabel: 'Create a Linear API key',
  },
];

type LoadState = 'loading' | 'error' | 'ready';
type Fields = Record<string, string | undefined>;

export interface TrackerSettingsProps {
  client?: ApiClient;
}

/**
 * Per-project issue-tracker connection (Stories #307/#47, FR-10.x). Org-admin only.
 * Bridges the UI to the existing secure tracker-settings backend: the API token is
 * write-only (sent once over HTTPS, never read back), and saving runs a LIVE probe
 * against the provider before the connection is enabled (fail-closed). Jira, GitHub,
 * and Linear are supported here.
 */
export function TrackerSettings({ client = defaultClient }: TrackerSettingsProps) {
  const { currentOrg } = useOrg();
  const { id: projectId } = useParams<{ id: string }>();
  const orgId = currentOrg?.id;
  const isAdmin = currentOrg?.role === 'org_admin';
  useFocusMain();

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [view, setView] = useState<TrackerSettingsView | null>(null);

  const [provider, setProvider] = useState<UiProvider>('jira');
  const [baseUrl, setBaseUrl] = useState('');
  const [email, setEmail] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [githubOwner, setGithubOwner] = useState('');
  const [githubRepo, setGithubRepo] = useState('');
  const [linearTeam, setLinearTeam] = useState('');
  const [apiToken, setApiToken] = useState('');

  const [fieldErrors, setFieldErrors] = useState<Fields>({});
  const [summary, setSummary] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  const hydrate = useCallback((v: TrackerSettingsView) => {
    setView(v);
    const p = (['jira', 'github', 'linear'] as const).includes(v.provider as UiProvider)
      ? (v.provider as UiProvider)
      : 'jira';
    setProvider(p);
    setBaseUrl(v.baseUrl ?? '');
    setEmail(v.email ?? '');
    setProjectKey(v.projectKey ?? '');
    setGithubOwner(v.githubOwner ?? '');
    setGithubRepo(v.githubRepo ?? '');
    setLinearTeam(v.linearTeam ?? '');
    setApiToken('');
  }, []);

  const load = useCallback(async () => {
    if (!orgId || !projectId || !isAdmin) return;
    setPhase('loading');
    try {
      hydrate(await client.getTrackerSettings(orgId, projectId));
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, projectId, isAdmin, hydrate]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const onProviderChange = (next: UiProvider) => {
    setProvider(next);
    // Switching provider starts a clean destination form (the old fields belong to
    // the previous provider) and always requires a fresh token.
    setBaseUrl('');
    setEmail('');
    setProjectKey('');
    setGithubOwner('');
    setGithubRepo('');
    setLinearTeam('');
    setApiToken('');
    setFieldErrors({});
    setSummary(null);
    setSuccess(null);
  };

  // The stored token can be reused only when re-saving the SAME, already-connected
  // provider; a fresh connect or a provider switch needs a token.
  const tokenRequired = !(view?.connected && provider === view.provider);
  const connectedToThisProvider = view?.connected === true && provider === view.provider;

  const selected = PROVIDERS.find((p) => p.id === provider)!;

  const applyError = (err: unknown) => {
    const fields = toFieldErrors(err);
    if (Object.keys(fields).length > 0) {
      setFieldErrors(fields);
      setSummary('Please fix the errors below and try again.');
      return;
    }
    // Tracker validation/probe failures return a 400 with a deliberately safe,
    // specific message (e.g. "Tracker authentication failed…") — surface it.
    if (isApiError(err) && (err.status === 400 || err.status === 422)) {
      setSummary(err.message);
    } else {
      setSummary(toDisplayError(err).message);
    }
  };

  const validate = (): Fields => {
    const errs: Fields = {};
    if (provider === 'jira') {
      if (!baseUrl.trim()) errs.baseUrl = 'Enter your Jira base URL (https).';
      if (!email.trim()) errs.email = 'Enter the account email for the API token.';
      if (!projectKey.trim()) errs.projectKey = 'Enter the destination project key, e.g. QA.';
    } else if (provider === 'github') {
      if (!baseUrl.trim()) errs.baseUrl = 'Enter the GitHub base URL (https).';
      if (!githubOwner.trim()) errs.githubOwner = 'Enter the repository owner.';
      if (!githubRepo.trim()) errs.githubRepo = 'Enter the repository name.';
    } else {
      if (!linearTeam.trim()) errs.linearTeam = 'Enter your Linear team key, e.g. ENG.';
    }
    if (tokenRequired && !apiToken.trim()) errs.apiToken = 'Enter an API token to connect.';
    return errs;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || !orgId || !projectId) return;
    setSuccess(null);

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSummary('Please fix the errors below and try again.');
      return;
    }
    setSummary(null);

    const input: UpdateTrackerSettingsInput = { provider, enabled: true };
    if (apiToken.trim()) input.apiToken = apiToken.trim();
    if (provider === 'jira') {
      input.baseUrl = baseUrl.trim();
      input.email = email.trim();
      input.projectKey = projectKey.trim();
    } else if (provider === 'github') {
      input.baseUrl = baseUrl.trim();
      input.githubOwner = githubOwner.trim();
      input.githubRepo = githubRepo.trim();
    } else {
      input.linearTeam = linearTeam.trim();
    }

    setBusy(true);
    try {
      const next = await client.updateTrackerSettings(orgId, projectId, input);
      hydrate(next);
      setSuccess('Connection validated and saved.');
      requestAnimationFrame(() => successRef.current?.focus());
    } catch (err) {
      applyError(err);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (busy || !orgId || !projectId) return;
    setSummary(null);
    setSuccess(null);
    setBusy(true);
    try {
      const next = await client.updateTrackerSettings(orgId, projectId, { clearConnection: true });
      hydrate(next);
      setSuccess('Tracker disconnected.');
      requestAnimationFrame(() => successRef.current?.focus());
    } catch (err) {
      applyError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={styles.heading}>Issue tracker</h1>
        <p className={styles.subheading}>
          Connect this project to an issue tracker so TestCraft can file defects it finds. We only
          create issues — nothing else in your tracker is touched.
        </p>

        {!isAdmin ? (
          <InlineAlert severity="info">
            Only an organization admin can configure the issue tracker.
          </InlineAlert>
        ) : (
          <>
            {phase === 'loading' ? (
              <div className={styles.loading}>
                <Spinner />
                <span>Loading tracker settings…</span>
              </div>
            ) : null}

            {phase === 'error' ? (
              <div className={styles.loadError}>
                <InlineAlert severity="error">{loadMessage}</InlineAlert>
                <Link to={`/projects/${projectId ?? ''}/edit`} className={styles.backLink}>
                  Back to project
                </Link>
              </div>
            ) : null}

            {phase === 'ready' ? (
              <>
                <p className={styles.subheading} data-testid="tracker-assurance">
                  {INTEGRATION_ASSURANCE}
                </p>

                <div className={styles.summary}>
                  <InlineAlert ref={summaryRef} severity="error">
                    {summary}
                  </InlineAlert>
                </div>
                <InlineAlert ref={successRef} severity="success">
                  {success}
                </InlineAlert>

                <p aria-live="polite" data-testid="tracker-status">
                  {connectedToThisProvider
                    ? `Connected to ${selected.label}.`
                    : 'Not connected.'}
                </p>

                <form onSubmit={submit} noValidate>
                  <SelectField
                    label="Provider"
                    id="tracker-provider"
                    value={provider}
                    onChange={(v) => onProviderChange(v as UiProvider)}
                    options={PROVIDERS.map((p) => ({ value: p.id, label: p.label }))}
                  />

                  <p className={styles.subheading}>
                    <a href={selected.keyUrl} target="_blank" rel="noreferrer noopener">
                      {selected.keyLabel}
                    </a>{' '}
                    <span aria-hidden="true">↗</span>
                  </p>

                  {provider !== 'linear' ? (
                    <FormField
                      label="Base URL"
                      id="tracker-base-url"
                      helpText={
                        provider === 'jira'
                          ? 'Your Jira site, e.g. https://acme.atlassian.net.'
                          : 'https://github.com, or your GitHub Enterprise host.'
                      }
                      {...(fieldErrors.baseUrl ? { error: fieldErrors.baseUrl } : {})}
                    >
                      {({ controlProps }) => (
                        <TextInput
                          {...controlProps}
                          type="text"
                          inputMode="url"
                          name="tracker-base-url"
                          autoComplete="off"
                          value={baseUrl}
                          onChange={(e) => setBaseUrl(e.target.value)}
                        />
                      )}
                    </FormField>
                  ) : null}

                  {provider === 'jira' ? (
                    <>
                      <FormField
                        label="Account email"
                        id="tracker-email"
                        helpText="The email of the account that owns the API token."
                        {...(fieldErrors.email ? { error: fieldErrors.email } : {})}
                      >
                        {({ controlProps }) => (
                          <TextInput
                            {...controlProps}
                            type="email"
                            name="tracker-email"
                            autoComplete="off"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        )}
                      </FormField>
                      <FormField
                        label="Project key"
                        id="tracker-project-key"
                        helpText="The destination Jira project key, e.g. QA."
                        {...(fieldErrors.projectKey ? { error: fieldErrors.projectKey } : {})}
                      >
                        {({ controlProps }) => (
                          <TextInput
                            {...controlProps}
                            type="text"
                            name="tracker-project-key"
                            autoComplete="off"
                            value={projectKey}
                            onChange={(e) => setProjectKey(e.target.value)}
                          />
                        )}
                      </FormField>
                    </>
                  ) : null}

                  {provider === 'github' ? (
                    <>
                      <FormField
                        label="Repository owner"
                        id="tracker-github-owner"
                        helpText="The user or organization that owns the repository."
                        {...(fieldErrors.githubOwner ? { error: fieldErrors.githubOwner } : {})}
                      >
                        {({ controlProps }) => (
                          <TextInput
                            {...controlProps}
                            type="text"
                            name="tracker-github-owner"
                            autoComplete="off"
                            value={githubOwner}
                            onChange={(e) => setGithubOwner(e.target.value)}
                          />
                        )}
                      </FormField>
                      <FormField
                        label="Repository name"
                        id="tracker-github-repo"
                        helpText="The repository where issues should be created."
                        {...(fieldErrors.githubRepo ? { error: fieldErrors.githubRepo } : {})}
                      >
                        {({ controlProps }) => (
                          <TextInput
                            {...controlProps}
                            type="text"
                            name="tracker-github-repo"
                            autoComplete="off"
                            value={githubRepo}
                            onChange={(e) => setGithubRepo(e.target.value)}
                          />
                        )}
                      </FormField>
                    </>
                  ) : null}

                  {provider === 'linear' ? (
                    <FormField
                      label="Team key"
                      id="tracker-linear-team"
                      helpText="Your Linear team key, e.g. ENG."
                      {...(fieldErrors.linearTeam ? { error: fieldErrors.linearTeam } : {})}
                    >
                      {({ controlProps }) => (
                        <TextInput
                          {...controlProps}
                          type="text"
                          name="tracker-linear-team"
                          autoComplete="off"
                          value={linearTeam}
                          onChange={(e) => setLinearTeam(e.target.value)}
                        />
                      )}
                    </FormField>
                  ) : null}

                  <FormField
                    label={connectedToThisProvider ? 'New API token' : 'API token'}
                    id="tracker-api-token"
                    helpText={
                      connectedToThisProvider
                        ? 'Stored encrypted. Leave blank to keep the current token.'
                        : 'Pasted once and stored encrypted. We never display it again.'
                    }
                    {...(fieldErrors.apiToken ? { error: fieldErrors.apiToken } : {})}
                  >
                    {({ controlProps }) => (
                      <TextInput
                        {...controlProps}
                        type="password"
                        name="tracker-api-token"
                        autoComplete="off"
                        value={apiToken}
                        onChange={(e) => setApiToken(e.target.value)}
                      />
                    )}
                  </FormField>

                  <div className={styles.actions}>
                    <Button type="submit" busy={busy}>
                      {busy ? 'Validating…' : connectedToThisProvider ? 'Save changes' : 'Connect'}
                    </Button>
                    {view?.connected ? (
                      <Button type="button" variant="danger" busy={busy} onClick={disconnect}>
                        Disconnect
                      </Button>
                    ) : null}
                  </div>
                </form>

                <Link to={`/projects/${projectId ?? ''}/edit`} className={styles.backLink}>
                  Back to project
                </Link>
              </>
            ) : null}
          </>
        )}
      </Card>
    </AppShell>
  );
}
